/** Load a commit's STATE from its packs (Tom, 2026-10-07; wire contract §2.1).
 *
 *  1. Ask our server for the commit's MANIFEST: it checks access and lists the
 *     packs, each with a short-lived signed S3 link (or its rows inline).
 *  2. Take each pack from the cache by its NAME, else fetch it straight from
 *     S3, at most `concurrency` at a time, without a referrer and without
 *     credentials, and parse its NDJSON as it arrives.
 *  3. A link that has expired (403): ask for the manifest again, ONCE per
 *     load, and fetch what is still missing with the new links. A pack that
 *     CANNOT BE FETCHED is never fatal — THE STORE IS NEVER NEEDED (Tom,
 *     2026-10-08: every reader falls back to the ops): gone (404, storage
 *     may be wiped at any time), a 403 again with fresh links, a 5xx, a
 *     network or CORS error, a stream cut mid-pack. Ask again NAMING it
 *     (`missing`): the server answers a base that avoids it — an older
 *     commit's, or no base with the whole line in `commits_after_base`
 *     (wire §2.1, v1.9c); a pack storage still holds (the browser cannot
 *     reach a store the server can) is answered from the ops alone
 *     (VC-D, pack key part B). Each re-ask names every pack that failed so
 *     far in the load. A re-ask that fails is tried once more; then the
 *     load fails and the open is tried again.
 *  4. ALL OR NOTHING: the state is answered only when every pack is in. A
 *     missing pack would read as deleted rows to the re-load's removal.
 *
 *  The answer is the shape the load path already applies
 *  (`{root_nodes, nodes, relationships}`), so the switch from today's
 *  bootstrap is one call. */

import { parseNdjsonStream, parseNdjson } from "./ndjson";
import { noPackCache, type PackCache } from "./pack-cache";

export type PackEntry = {
  kind: "node" | "edge";
  type: string;
  prefix?: string;
  rows: number;
  name: string;
  url?: string;
  inline?: unknown[];
};

export type StateManifest = {
  fs: string;
  island: string;
  line: string;
  /** null: no commit of the line has a manifest in storage — the whole line
   *  is in `commits_after_base` (wire §2.1, v1.9) */
  commit: string | null;
  as_of: number;
  expires_at: number;
  root: string;
  packs: PackEntry[];
  /** the commits after this base, to apply on it in order (v1.9: the
   *  manifest route carries them too, for a re-ask) */
  commits_after_base?: string[];
};

export type LoadedState = {
  root_nodes: Record<string, unknown>[];
  nodes: Record<string, unknown>[];
  relationships: Record<string, unknown>[];
  fs: string;
  commit: string | null;
  as_of: number;
  /** the manifest's own list, when it carries one (a re-asked manifest can
   *  name a newer base than the open did) */
  commits_after_base?: string[];
  /** the base this state was built from, by its packs' names: what the
   *  island store's head records (every pack is in the pack cache) */
  base: { commit: string | null; root: string; packs: { name: string; kind: string }[] };
};

export class StatePacksError extends Error {
  constructor(message: string, public readonly pack?: string) {
    super(message);
    this.name = "StatePacksError";
  }
}

export type LoadOptions = {
  /** reads the manifest from our server (credentials included); `missing`:
   *  the names of packs that could not be fetched, for a base that avoids
   *  them */
  getManifest: (ask?: { missing?: string[] }) => Promise<StateManifest>;
  /** the manifest the open answered inline (`state.manifest`): used first,
   *  so a fresh open costs no extra request */
  initial?: StateManifest;
  fetch?: typeof fetch;
  cache?: PackCache;
  concurrency?: number;
  now?: () => number;
  /** re-ask for the manifest before starting when its links expire sooner */
  expiryMarginMs?: number;
};

class Expired extends Error {}
/** A pack that could not be fetched, whatever the cause. Carries no detail:
 *  a signed URL is a bearer credential, and a runtime's network error can
 *  carry it (in its message or cause). */
class Failed extends Error {}

/** At most this many names per re-ask (the route's limit), and this many
 *  re-asks for failed packs per load: storage failing faster than that is a
 *  failed load, and the open is tried again. */
const MISSING_PER_ASK = 16;
const GONE_ROUNDS = 4;
/** a re-ask that fails is tried once more after this pause */
const REASK_RETRY_MS = 1_000;

async function fetchPack(f: typeof fetch, url: string): Promise<{ rows: unknown[]; chars: number }> {
  let r: Response;
  try {
    r = await f(url, { credentials: "omit", referrerPolicy: "no-referrer", cache: "default" });
  } catch {
    throw new Failed(); // network, CORS, a timeout
  }
  if (r.status === 403) throw new Expired();
  if (!r.ok) throw new Failed(); // 404 gone, 5xx, any other refusal
  const stats = { chars: 0 };
  try {
    const rows = r.body ? await parseNdjsonStream(r.body, stats) : parseNdjson(await r.text(), stats);
    return { rows, chars: stats.chars };
  } catch {
    throw new Failed(); // the stream cut mid-pack, or rows that do not parse
  }
}

/** ONE FETCH PER PACK NAME in flight on this page: a load and a prefetch
 *  (state-packs/prefetch) asking for the same pack share it. Keyed by name,
 *  not link: a pack's link is signed per manifest. */
const inFlight = new Map<string, Promise<{ rows: unknown[]; chars: number }>>();
function fetchPackOnce(f: typeof fetch, name: string, url: string): Promise<{ rows: unknown[]; chars: number }> {
  const held = inFlight.get(name);
  if (held) return held;
  const p = fetchPack(f, url).finally(() => { if (inFlight.get(name) === p) inFlight.delete(name); });
  inFlight.set(name, p);
  return p;
}

/** Run `tasks` with at most `limit` in flight. */
async function pooled<T>(tasks: (() => Promise<T>)[], limit: number): Promise<T[]> {
  const out: T[] = new Array(tasks.length);
  let next = 0;
  const worker = async () => {
    while (next < tasks.length) {
      const i = next++;
      out[i] = await tasks[i]();
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
  return out;
}

export async function loadStateFromPacks(opts: LoadOptions): Promise<LoadedState> {
  const f = opts.fetch ?? fetch;
  const cache = opts.cache ?? noPackCache;
  const limit = opts.concurrency ?? 6;
  const now = opts.now ?? Date.now;
  const margin = opts.expiryMarginMs ?? 30_000;

  let manifest = opts.initial ?? (await opts.getManifest());
  let reasked = false;
  let goneRounds = 0;
  // every pack that failed so far in this load: each re-ask names them ALL —
  // the server's answer avoids only the names it is sent, and an older base
  // often shares a pack reported in an earlier round (wire §2.1, v1.9c)
  const goneSoFar: string[] = [];
  const reaskNaming = async (missing: string[]) => {
    try {
      return await opts.getManifest({ missing });
    } catch {
      await new Promise((resolve) => setTimeout(resolve, REASK_RETRY_MS));
      return opts.getManifest({ missing });
    }
  };

  const rowsByName = new Map<string, unknown[]>();
  const fetched = new Set<string>();
  /** each fetched pack's decoded size, for the persistent cache's bound */
  const sizeOf = new Map<string, number>();
  const takeKnown = async () => {
    for (const p of manifest.packs) {
      if (p.inline) rowsByName.set(p.name, p.inline);
    }
    await Promise.all(
      manifest.packs
        .filter((p) => !rowsByName.has(p.name))
        .map(async (p) => {
          const hit = await cache.get(p.name).catch(() => undefined);
          if (hit) rowsByName.set(p.name, hit);
        }),
    );
  };
  await takeKnown();
  // links expiring soon matter only when a pack must still be fetched: a
  // manifest whose packs are all inline or cached is used as it is
  if (manifest.packs.some((p) => !rowsByName.has(p.name)) && manifest.expires_at - now() < margin) {
    manifest = await opts.getManifest();
    reasked = true;
    await takeKnown();
  }

  // fetch what is missing; on expiry re-ask once, on a failed pack re-ask
  // naming it, and fetch the rest again
  for (;;) {
    const missing = manifest.packs.filter((p) => !rowsByName.has(p.name));
    if (missing.length === 0) break;
    let expired = false;
    const gone: string[] = [];
    await pooled(
      missing.map((p) => async () => {
        try {
          if (!p.url) throw new Failed(); // neither a link nor its rows
          const got = await fetchPackOnce(f, p.name, p.url);
          rowsByName.set(p.name, got.rows);
          sizeOf.set(p.name, got.chars);
          fetched.add(p.name);
        } catch (e) {
          // an expired link is re-asked for once; a 403 with fresh links is
          // a pack this browser may not fetch: named like any other failure
          if (e instanceof Expired && !reasked) expired = true;
          else gone.push(p.name);
        }
      }),
      limit,
    );
    if (gone.length) {
      if (goneRounds >= GONE_ROUNDS) throw new StatePacksError("packs keep failing — re-open");
      goneRounds++;
      for (const name of gone) if (!goneSoFar.includes(name)) goneSoFar.push(name);
      manifest = await reaskNaming(goneSoFar.slice(-MISSING_PER_ASK));
      await takeKnown();
      continue;
    }
    if (!expired) continue;
    manifest = await opts.getManifest();
    reasked = true;
    // the new manifest may name other packs (a newer base, v1.9): take its
    // inline rows and what the cache holds before fetching the rest
    await takeKnown();
  }

  // every pack is in: build the state, then cache what was fetched
  const root_nodes: Record<string, unknown>[] = [];
  const nodes: Record<string, unknown>[] = [];
  const relationships: Record<string, unknown>[] = [];
  for (const p of manifest.packs) {
    const rows = rowsByName.get(p.name)! as Record<string, unknown>[];
    if (p.kind === "edge") {
      for (const r of rows) relationships.push(r);
    } else {
      for (const r of rows) (r.id === manifest.root ? root_nodes : nodes).push(r);
    }
  }
  await Promise.all(
    Array.from(fetched).map((name) => cache.put(name, rowsByName.get(name)!, sizeOf.get(name)).catch(() => undefined)),
  );
  // the inline packs too: a refresh rebuilds this base from the pack cache
  // alone (the island store's head names every pack)
  await Promise.all(
    manifest.packs.filter((p) => p.inline && !fetched.has(p.name)).map((p) => cache.put(p.name, p.inline!).catch(() => undefined)),
  );
  return {
    root_nodes, nodes, relationships, fs: manifest.fs, commit: manifest.commit, as_of: manifest.as_of,
    ...(manifest.commits_after_base ? { commits_after_base: manifest.commits_after_base } : {}),
    base: { commit: manifest.commit, root: manifest.root, packs: manifest.packs.map((p) => ({ name: p.name, kind: p.kind })) },
  };
}
